'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AiFillCheckCircle } from 'react-icons/ai';
import { FiX } from 'react-icons/fi';
import { GoAlertFill } from 'react-icons/go';
import { MdCancel } from 'react-icons/md';
import { toast } from 'react-toastify';
import { match } from 'ts-pattern';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Input } from '@/components/input/input';
import Modal from '@/components/modal/modal';
import {
  EXAMPLE_TYPES,
  Source,
  SOURCE_TYPE_DISPLAY_NAMES,
  SOURCE_TYPE_HELPER,
  SOURCE_TYPES,
  SourceCreateReq,
  SourceTypeEnum
} from '@/types/evals';

import { JsonConfigFields, JsonValidity } from './JsonConfigForm';
import { cleanMasConfig, logTypeForSourceType, MasLogsConfigFields } from './MasLogsConfigForm';
import styles from './modalSourceCreate.module.scss';
import {
  agentActionLogsConfigSchema,
  completionLogsConfigSchema,
  FullSourceFormData,
  fullSourceFormSchema,
  modelInvocationLogsConfigSchema,
  snowflakeConfigSchema
} from './schemas';
import { SnowflakeConfigFields } from './SnowflakeConfigForm';

type ModalSourceCreateProps = {
  cbAction: ( data: SourceCreateReq ) => void;
  onCloseAction: () => void;
  sourceToEdit?: Source;
  isPending?: boolean;
};

type ValidationState = 'idle' | 'ok' | 'error';

const SOURCE_TYPE_OPTIONS: SourceTypeEnum[] = [
  SOURCE_TYPES.JSON,
  SOURCE_TYPES.COMPLETION_LOGS,
  SOURCE_TYPES.MODEL_INVOCATION_LOGS,
  SOURCE_TYPES.AGENT_ACTION_LOGS,
  SOURCE_TYPES.SNOWFLAKE
];

const normalizeConfig = ( config: unknown ): Record<string, unknown> => {
  const c = ( config ?? {} ) as Record<string, unknown>;
  if ( 'row_limit' in c && c.row_limit === null ) {
    return { ...c, row_limit: undefined };
  }
  return c;
};

// True once the user has typed anything into the config fields, so the
// "validate to continue" prompt only appears after data is entered.
const hasConfigValues = ( value: unknown ): boolean => {
  if ( value === undefined || value === null ) return false;
  if ( typeof value === 'string' ) return value.trim() !== '';
  if ( typeof value === 'object' ) return Object.values( value ).some( hasConfigValues );
  return true;
};

export const ModalSourceCreate: React.FC<ModalSourceCreateProps> = ( {
  cbAction,
  onCloseAction,
  sourceToEdit,
  isPending
} ) => {
  const isVersionMode = !!sourceToEdit;

  const methods = useForm<FullSourceFormData>( {
    resolver: zodResolver( fullSourceFormSchema ),
    defaultValues: sourceToEdit
      ? {
          name: sourceToEdit.name,
          description: sourceToEdit.description,
          source_type: sourceToEdit.source_type,
          example_type: sourceToEdit.example_type,
          config: normalizeConfig( sourceToEdit.config )
        }
      : {
          name: '',
          description: '',
          source_type: undefined as unknown as SourceTypeEnum,
          example_type: EXAMPLE_TYPES.CONVERSATION,
          config: {}
        }
  } );

  const { register, handleSubmit, watch, setValue, trigger, getValues, formState: { errors } } = methods;

  const sourceType = watch( 'source_type' );
  const isJson = sourceType === SOURCE_TYPES.JSON;
  const masLogType = sourceType ? logTypeForSourceType( sourceType ) : undefined;
  const configValues = watch( 'config' );
  const hasConfigData = hasConfigValues( configValues );

  // The Validate button stays disabled until the connection's required fields
  // are present, mirroring the per-source-type config schemas.
  const configReady = match( sourceType )
    .with( SOURCE_TYPES.SNOWFLAKE, () => snowflakeConfigSchema.safeParse( configValues ).success )
    .with( SOURCE_TYPES.AGENT_ACTION_LOGS, () => agentActionLogsConfigSchema.safeParse( configValues ).success )
    .with( SOURCE_TYPES.MODEL_INVOCATION_LOGS, () => modelInvocationLogsConfigSchema.safeParse( configValues ).success )
    .with( SOURCE_TYPES.COMPLETION_LOGS, () => completionLogsConfigSchema.safeParse( configValues ).success )
    .otherwise( () => false );

  const [ validationState, setValidationState ] = useState<ValidationState>( 'idle' );
  const [ validationMessage, setValidationMessage ] = useState<string>();
  const [ isValidating, setIsValidating ] = useState( false );
  const [ jsonValidity, setJsonValidity ] = useState<JsonValidity>( { blocked: true } );
  const [ showErrorDetail, setShowErrorDetail ] = useState( false );
  const isMountedRef = useRef( true );

  useEffect( () => {
    return () => { isMountedRef.current = false; };
  }, [] );

  // Re-validation is required after any config edit or type change.
  useEffect( () => {
    const sub = watch( ( _value, { name } ) => {
      if ( name === 'source_type' || name?.startsWith( 'config' ) ) {
        setValidationState( 'idle' );
        setValidationMessage( undefined );
      }
    } );
    return () => sub.unsubscribe();
  }, [ watch ] );

  // ESC closes the modal.
  useEffect( () => {
    const handleKeyDown = ( event: KeyboardEvent ) => {
      if ( event.key === 'Escape' && !isPending ) onCloseAction();
    };
    window.addEventListener( 'keydown', handleKeyDown );
    return () => window.removeEventListener( 'keydown', handleKeyDown );
  }, [ onCloseAction, isPending ] );

  const handleSelectType = ( value: SourceTypeEnum ) => {
    setValue( 'source_type', value, { shouldValidate: false } );
    setValue( 'config', {} );
    setValue( 'example_type', EXAMPLE_TYPES.CONVERSATION );
    setValidationState( 'idle' );
    setValidationMessage( undefined );
    setJsonValidity( { blocked: true } );
  };

  const handleValidate = async() => {
    const isFormValid = await trigger();
    if ( !isFormValid ) return;

    setIsValidating( true );
    setValidationMessage( undefined );
    setValidationState( 'idle' );
    setShowErrorDetail( false );

    try {
      const values = getValues();
      const config = masLogType ? cleanMasConfig( values.config as never, undefined, masLogType ) : values.config;
      const result = await APIGateway.evals.sourceValidate( {
        name: values.name,
        description: values.description,
        source_type: values.source_type,
        config,
        example_type: values.example_type
      } );

      if ( !isMountedRef.current ) return;
      setIsValidating( false );
      if ( result.status === 'success' ) {
        setValidationState( 'ok' );
      } else {
        setValidationState( 'error' );
        setValidationMessage( result.message );
      }
    } catch( error ) {
      if ( !isMountedRef.current ) return;
      setIsValidating( false );
      setValidationState( 'error' );
      setValidationMessage(
        error instanceof Error ? error.message : 'Validation failed. Please check your configuration and try again.'
      );
    }
  };

  const onSubmit = ( data: FullSourceFormData ) => {
    try {
      if ( data.source_type === SOURCE_TYPES.JSON ) {
        const jsonConfig = data.config as { examples: unknown[] };
        cbAction( {
          name: data.name,
          description: data.description,
          source_type: data.source_type,
          examples: jsonConfig.examples
        } as unknown as SourceCreateReq );
        return;
      }

      const config = masLogType ? cleanMasConfig( data.config as never, undefined, masLogType ) : data.config;
      cbAction( {
        name: data.name,
        description: data.description,
        source_type: data.source_type,
        config,
        example_type: data.example_type
      } as SourceCreateReq );
    } catch( error ) {
      toast.error( error instanceof Error ? error.message : 'Unable to build configuration' );
    }
  };

  const createDisabled = isPending || ( isJson
    ? jsonValidity.blocked
    : validationState === 'idle' );

  const headerTitle = isVersionMode
    ? `Version ${ ( sourceToEdit.latest_version_number ?? sourceToEdit.version ) + 1 } of ${ sourceToEdit.name }`
    : 'Create New Source';
  const headerSubtitle = isVersionMode
    ? 'Sources are used to run evaluations and generate results.'
    : 'Create a data source used to run evaluations and generate results.';

  const renderConfigSection = () => {
    if ( !sourceType ) return null;
    return match( sourceType )
      .with( SOURCE_TYPES.SNOWFLAKE, () => (
        <SnowflakeConfigFields isPending={ isPending } isVersionMode={ isVersionMode } />
      ) )
      .with( SOURCE_TYPES.JSON, () => (
        <JsonConfigFields
          isPending={ isPending }
          isVersionMode={ isVersionMode }
          sourceId={ sourceToEdit?.id }
          onValidityChange={ setJsonValidity }
        />
      ) )
      .with( SOURCE_TYPES.AGENT_ACTION_LOGS, () => (
        <MasLogsConfigFields logType="agent" isPending={ isPending } />
      ) )
      .with( SOURCE_TYPES.MODEL_INVOCATION_LOGS, () => (
        <MasLogsConfigFields logType="model" isPending={ isPending } />
      ) )
      .with( SOURCE_TYPES.COMPLETION_LOGS, () => (
        <MasLogsConfigFields logType="completion" isPending={ isPending } isVersionMode={ isVersionMode } />
      ) )
      .exhaustive();
  };

  const gateHint = isVersionMode
    ? 'Complete required fields to create a version.'
    : 'Complete required fields to create a source.';

  // Footer status mirrors the user's progress through the flow: prompt for
  // required fields, then to validate once config data exists, then the
  // validation outcome.
  const renderFooterStatus = () => {
    if ( sourceType && !isJson ) {
      return match( validationState )
        .with( 'ok', () => (
          <span className={ styles.footer_status_success } data-testid="validation-success-indicator">
            <AiFillCheckCircle aria-hidden /> Source validated.
          </span>
        ) )
        .with( 'error', () => (
          <span className={ styles.footer_status_error } data-testid="validation-error-message">
            <MdCancel aria-hidden /> Source had a connection{' '}
            {validationMessage ? (
              <button
                type="button"
                className={ styles.error_link }
                onClick={ () => setShowErrorDetail( true ) }
                data-testid="validation-error-detail-link"
              >
                error
              </button>
            ) : (
              'error.'
            )}
          </span>
        ) )
        .with( 'idle', () => ( hasConfigData ? (
          <span className={ styles.footer_status_pending }>
            <GoAlertFill aria-hidden /> Validate source configuration to continue.
          </span>
        ) : (
          <span className={ styles.footer_hint } data-testid="create-gate-hint">{gateHint}</span>
        ) ) )
        .exhaustive();
    }

    return createDisabled
      ? <span className={ styles.footer_hint } data-testid="create-gate-hint">{gateHint}</span>
      : null;
  };

  return (
    <>
      <Modal>
        <FormProvider { ...methods }>
          <form
            id="source-config-form"
            onSubmit={ handleSubmit( onSubmit ) }
            className={ styles.modal_wrapper }
          >
            <div className={ styles.modal_header }>
              <h2>{headerTitle}</h2>
              <p className={ styles.modal_subtitle }>{headerSubtitle}</p>
            </div>

            <div className={ styles.modal_content }>
              {!isVersionMode && (
                <div className={ clsx( 'layout__col', 'pad' ) }>
                  <div className={ styles.label_row }>
                    <label
                      className={ clsx( 'auth_flow_label', errors.name && 'required' ) }
                      htmlFor="name"
                    >
                      Source Name <span className={ styles.required_asterisk }>*</span>
                    </label>
                    <span className={ styles.char_limit }>Max 50 characters</span>
                  </div>
                  <Input
                    id="name"
                    type="text"
                    maxLength={ 50 }
                    placeholder={ 'Ex. "Test conversation data"' }
                    data-testid="source-name-input"
                    disabled={ isPending }
                    error={ errors.name && ( errors.name.message || 'This field is required' ) }
                    { ...register( 'name' ) }
                  />
                </div>
              )}

              <div className={ clsx( 'layout__col', 'pad' ) }>
                <div className={ styles.label_row }>
                  <label className="auth_flow_label" htmlFor="description">Description (optional)</label>
                  <span className={ styles.char_limit }>Max 150 characters</span>
                </div>
                <textarea
                  className={ styles.description_textarea }
                  id="description"
                  rows={ 3 }
                  maxLength={ 150 }
                  placeholder="Optional description for this source"
                  data-testid="source-description-input"
                  disabled={ isPending }
                  { ...register( 'description' ) }
                />
              </div>

              <div className={ clsx( 'layout__col', 'pad' ) } data-testid="source-type-selection">
                <label className={ clsx( 'auth_flow_label', errors.source_type && 'required' ) }>
                  Select Source Type <span className={ styles.required_asterisk }>*</span>
                </label>
                {isVersionMode ? (
                  <div className={ styles.locked_type } data-testid="source-type-locked">
                    {sourceType ? SOURCE_TYPE_DISPLAY_NAMES[ sourceType ] : ''}
                  </div>
                ) : (
                  <Menu
                    variant="select"
                    className={ clsx( styles.source_type_select, !sourceType && styles.source_type_placeholder ) }
                    value={ sourceType ? SOURCE_TYPE_DISPLAY_NAMES[ sourceType ] : 'Select Source Type' }
                    data-testid="source-type-dropdown"
                    disabled={ isPending }
                  >
                    {SOURCE_TYPE_OPTIONS.map( ( type ) => (
                      <MenuItem
                        key={ type }
                        value={ SOURCE_TYPE_DISPLAY_NAMES[ type ] }
                        description={ SOURCE_TYPE_HELPER[ type ] }
                        data-testid={ `source-type-option-${ type }` }
                        onClick={ () => handleSelectType( type ) }
                      />
                    ) )}
                  </Menu>
                )}
                {errors.source_type && (
                  <div className={ styles.error_message }>
                    {errors.source_type.message || 'Please select a source type'}
                  </div>
                )}
              </div>

              {renderConfigSection()}
            </div>

            {isJson && jsonValidity.message && (
              <div className={ styles.json_alert } data-testid="json-parse-error" role="alert">
                {jsonValidity.message}
              </div>
            )}

            <div className={ styles.modal_footer }>
              <div className={ styles.footer_status }>{renderFooterStatus()}</div>
              <div className={ clsx( 'layout__row', 'pad', 'flex_right', styles.modal_actions ) }>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={ onCloseAction }
                  disabled={ isPending }
                  data-testid="source-cancel-button"
                >
                  Cancel
                </Button>
                {sourceType && !isJson && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={ handleValidate }
                    loading={ isValidating }
                    disabled={ isValidating || isPending || !configReady }
                    data-testid="source-validate-button"
                  >
                    Validate Configuration
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  loading={ isPending }
                  disabled={ createDisabled }
                  data-testid="source-create-button"
                >
                  {isVersionMode ? 'Create New Version' : 'Create Source'}
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </Modal>

      {showErrorDetail && validationState === 'error' && validationMessage && (
        <Modal className={ styles.error_modal }>
          <div className={ styles.error_modal_header }>
            <h2>Connection Error</h2>
            <button
              type="button"
              className={ styles.error_modal_close }
              onClick={ () => setShowErrorDetail( false ) }
              aria-label="Close error details"
              data-testid="validation-error-detail-close"
            >
              <FiX />
            </button>
          </div>
          <pre className={ styles.error_modal_body } data-testid="validation-error-detail-message">
            {validationMessage}
          </pre>
          <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
            <Button variant="secondary" size="sm" onClick={ () => setShowErrorDetail( false ) }>
              Close
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ModalSourceCreate;
